// --------------------------------------------------------

import { CalendarMonth, ChevronLeft, Timer } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import ErrorArea from "../../components/error/ErrorArea";
import DetailScheduleUnmark from "../../components/SchedulingCard/DetailScheduleUnmark/DetailScheduleUnmark";
import ContainerWithSidebar from "../../components/UI/containerWithSidebar/ContainerWithSidebar";
import {
  GeneralContainer,
  H1,
  Span,
  SubTitleSimple,
  TextLink,
} from "../../components/UI/generalLayout/Layout.styled";
import { Spacer } from "../../components/UI/spacer/spacer";
import BackButton from "../../components/UI/buttons/BackButton";
import Colors from "../../constants/Colors";
import {
  DetailSchedule,
  SchedulingService,
} from "../../core/http/service/SchedulingService";
import { Paths } from "../../enums/Paths";

import {
  dateToBrazilianDate,
  dateToBrazilianHour,
  isOutdated,
} from "../../utils/date.utils";
import { Button } from "../company/companyRegister/CompanyRegister.styled";
import {
  CardScheduling,
  GoBackLinkContainer,
  MedicScheduleContainer,
  MedicScheduleContent,
  MedicScheduleImage,
  ScheduleDayCard,
  SchedulingDetailText,
} from "./Scheduling.styled";
import * as DOMPurify from "dompurify";
import useFetchPhoto from "../../hooks/useFetchPhoto";

// --------------------------------------------------------
// DetailSchedulingPage INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function DetailSchedulingPage(): JSX.Element {
  // --------------------------------------------------------
  // DetailSchedulingPage PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<DetailSchedule>();
  const [error, setError] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [photo, setPhoto] = useState<any>();
  const navigate = useNavigate();
  const { state } = useLocation();

  const { id } = (state as any) ?? {};
  const { horario, profissional, tipoConsulta, unidadeAtendimento } =
    detail ?? {};
  const { endereco, nome: nomeUnidade } = unidadeAtendimento ?? {};

  useFetchPhoto(profissional?.foto ?? "", setPhoto);

  useEffect(() => {
    if (id == null) {
      navigate(Paths.CollaboratorInitial);
    } else {
      const service = new SchedulingService();
      setLoading(true);
      service
        .getConsultByID(id)
        .then(({ data: { data } }) => {
          setDetail(data);
        })
        .catch(e => {
          setError([e]);
        })
        .finally(() => setLoading(false));
    }
  }, [id, navigate]);

  const fetchInformations = () => {
    const complement =
      endereco?.complemento !== null ? endereco?.complemento : "";
    const number =
      endereco?.numero !== null
        ? endereco?.numero + (complement !== "" ? "," : "")
        : "";
    const logradouro =
      endereco?.logradouro +
      (complement !== "" ? "," : "" || number !== "" ? "," : "");

    return (
      <>
        {logradouro} {number} {complement} - {endereco?.bairro},{" "}
        {endereco?.cidade} - {endereco?.estado}, {endereco?.cep}
      </>
    );
  };

  return (
    <ContainerWithSidebar>
      <GeneralContainer>
        <ErrorArea
          errorList={error}
          onCloseAction={() => {
            setError([]);
          }}
        />
        <>
          <BackButton onClick={() => navigate(-1)} />

          <Spacer top={24} />
          {loading ? (
            <CircularProgress />
          ) : detail ? (
            <CardScheduling>
              <H1>Consulta agendada</H1>
              <MedicScheduleContainer>
                <MedicScheduleContent>
                  {profissional?.foto ? (
                    <MedicScheduleImage src={photo} alt={profissional?.nome} />
                  ) : null}
                  <div>
                    <SchedulingDetailText bold color={Colors.SPM500}>
                      {profissional?.nome}
                    </SchedulingDetailText>
                    <SubTitleSimple>
                      {profissional?.especialidade}
                    </SubTitleSimple>
                  </div>
                </MedicScheduleContent>
                <ScheduleDayCard>
                  <CalendarMonth />
                  {dateToBrazilianDate(horario ?? "")}
                  <Spacer right={10} />
                  <Timer />
                  {dateToBrazilianHour(horario ?? "")}
                </ScheduleDayCard>
              </MedicScheduleContainer>

              <Spacer top={32} />
              <SchedulingDetailText bold>
                Endereço da unidade
              </SchedulingDetailText>
              <Span>{nomeUnidade}</Span>
              <Span>{fetchInformations()}</Span>
              <Spacer top={32} />
              <SchedulingDetailText bold>
                Tipo de atendimento
              </SchedulingDetailText>
              <Span>{tipoConsulta?.nome}</Span>
              <Spacer top={32} />
              <SchedulingDetailText bold>
                Instruções para consulta
              </SchedulingDetailText>
              <Span
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(tipoConsulta?.orientacoes ?? ""),
                }}
              />
              <Spacer top={32} />
              {!isOutdated(horario ?? "") ? (
                <Button
                  onClick={() => {
                    setModalOpen(true);
                  }}
                  color={Colors.error}
                  backgroundColor={Colors.WHITE}
                  borderColor={Colors.error}
                >
                  Desmarcar consulta
                </Button>
              ) : null}
              <DetailScheduleUnmark
                id={id}
                onCancelModal={() => setModalOpen(false)}
                modalOpen={modalOpen}
              />
            </CardScheduling>
          ) : null}
        </>
      </GeneralContainer>
    </ContainerWithSidebar>
  );
}

export default DetailSchedulingPage;
